/**
 * PDF preview — Step 73 (Kai: "I would like to be able to preview PDFs,
 * everything in the web GUI, is there a way to do this safely?").
 *
 * There is, and this is it. The danger was never PDFs as such — it is the
 * BROWSER'S BUILT-IN VIEWER, which executes a PDF's embedded JavaScript in
 * the origin that served the file. That is why `/api/media/:id` sends
 * `Content-Disposition: attachment` for everything except images, and why
 * that stays exactly as it is.
 *
 * So we never let the browser open the PDF. We fetch the BYTES and render
 * them ourselves:
 *
 *   1. `fetch()` reads the file fine despite the attachment disposition —
 *      that header governs navigation and embedding, not reading. So the
 *      download-only rule is preserved while preview still works.
 *   2. pdf.js parses in a worker and paints to a <canvas>. A canvas cannot
 *      execute anything; the output is pixels.
 *   3. `isEvalSupported: false` closes the CVE-2024-4367 class, where a
 *      crafted font could reach arbitrary evaluation.
 *   4. Scripting is OFF (pdf.js default, set explicitly here so nobody
 *      "helpfully" turns it on): a PDF's own JavaScript never runs, and
 *      neither do its actions, embedded files or XFA forms.
 *   5. External resource loading is disabled — no PDF can phone home to
 *      report that someone opened it.
 *
 * Bare fetch on purpose: this reads raw BYTES into an ArrayBuffer for the
 * PDF parser. apiFetch/portalFetch both parse JSON, and the media route is
 * readable from either domain (it authorises per owner, outside the wall),
 * so there is no domain-specific helper to use. A 401 cannot occur here —
 * the id only reaches this component from an already-authorised list.
 *
 * The annotation and XFA HTML layers are deliberately NOT rendered. They are
 * the parts of pdf.js that inject markup, and a link layer is not worth the
 * surface; a reader who needs the real document downloads it.
 */
import { useEffect, useRef, useState } from 'react';
import * as pdfjs from 'pdfjs-dist';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';

// Bundled by Vite, so the worker is same-origin — never a CDN, which would
// be a third party executing in our page.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

/** Everything that must be true for a PDF to be inert. Kept in one object so
 *  the guarantee is reviewable in one place rather than spread over a call. */
const SAFE_DOCUMENT_OPTIONS = {
  /** A PDF's own JavaScript, actions and XFA forms never run. */
  enableScripting: false,
  enableXfa: false,
  /** Closes the crafted-font evaluation class (CVE-2024-4367). */
  isEvalSupported: false,
  /** No fetching anything the PDF points at — no beacons, no tracking pixels
   *  reporting that a document was opened. */
  disableExternalLinkTarget: true,
  isOffscreenCanvasSupported: false,
  /** Standard fonts ship with the bundle; nothing is fetched from a CDN. */
  useSystemFonts: false,
} as const;

export function PdfPreview({ url, className }: { url: string; className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const docRef = useRef<pdfjs.PDFDocumentProxy | null>(null);
  // URL state on purpose: the page number inside an attachment preview. The
  // ATTACHMENT is what belongs in the URL; which page of it you scrolled to is
  // transient, and it resets whenever the viewer is reopened.
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let task: pdfjs.PDFDocumentLoadingTask | null = null;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Bare fetch on purpose (see the file header): raw bytes, not JSON.
        // `Content-Disposition: attachment` does not block this — it blocks
        // NAVIGATION, which is the thing we do not want.
        const res = await fetch(url, { credentials: 'same-origin' });
        if (!res.ok) throw new Error(res.status === 404 ? 'Not available' : `Could not load (${res.status})`);
        const data = new Uint8Array(await res.arrayBuffer());
        if (cancelled) return;

        task = pdfjs.getDocument({ data, ...SAFE_DOCUMENT_OPTIONS });
        const doc = await task.promise;
        if (cancelled) return; // the task teardown below releases it

        docRef.current = doc;
        setPages(doc.numPages);
        setPage(1);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not open this PDF.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      // `destroy()` lives on the LOADING TASK in pdf.js 6, and tearing it
      // down releases the worker — without this, opening several attachments
      // leaks one worker each. `cleanup()` on the document only frees the
      // rendering caches, so it is not a substitute.
      void docRef.current?.cleanup();
      void task?.destroy();
      docRef.current = null;
    };
  }, [url]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const doc = docRef.current;
      const canvas = canvasRef.current;
      if (!doc || !canvas || pages === 0) return;
      const p = await doc.getPage(page);
      if (cancelled) return;

      // Render at the device's real pixel density, then size the element in
      // CSS pixels — otherwise text is soft on a retina screen.
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const base = p.getViewport({ scale: 1 });
      const targetWidth = canvas.parentElement?.clientWidth ?? 800;
      const scale = Math.min(targetWidth / base.width, 2);
      const viewport = p.getViewport({ scale: scale * dpr });

      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.style.width = `${viewport.width / dpr}px`;
      canvas.style.height = `${viewport.height / dpr}px`;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      await p.render({ canvas, canvasContext: ctx, viewport }).promise;
    })();
    return () => {
      cancelled = true;
    };
  }, [page, pages]);

  if (error) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
        {error}
      </div>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex min-h-[12rem] items-center justify-center overflow-auto rounded-md border bg-neutral-100 p-3 dark:bg-neutral-900">
        {loading ? (
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Opening…
          </span>
        ) : (
          // A canvas of pixels. Nothing in the PDF can execute here.
          <canvas ref={canvasRef} className="max-w-full rounded shadow-sm" />
        )}
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-center gap-3 text-sm">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((n) => Math.max(1, n - 1))}
          >
            <ChevronLeft /> Previous
          </Button>
          <span className="tabular-nums text-muted-foreground">
            Page {page} of {pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= pages}
            onClick={() => setPage((n) => Math.min(pages, n + 1))}
          >
            Next <ChevronRight />
          </Button>
        </div>
      )}
    </div>
  );
}
